/* eslint-disable functional/immutable-data -- Browser MediaRecorder, SpeechRecognition, and FileReader APIs require property assignment for event handlers */
/* eslint-disable @typescript-eslint/no-explicit-any -- SpeechRecognition is not in standard TS lib types */
/**
 * Hook for managing audio recording with real-time frequency analysis.
 *
 * On native (iOS/Android): uses expo-speech-recognition for on-device
 * speech-to-text. Only the transcript is captured — no audio file is produced.
 * On web: uses browser MediaRecorder API for real mic recording +
 * SpeechRecognition API for live transcript during recording +
 * Web Audio API AnalyserNode for real-time frequency levels.
 *
 * expo-speech-recognition is dynamically imported to avoid
 * breaking the web bundler which cannot resolve native modules.
 * @module features/voice-report/hooks/useAudioRecorder
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Platform } from "react-native";

import {
  dictationElapsedSecondsVar,
  dictationLiveTranscriptVar,
  dictationStatusVar,
} from "../stores/dictationStore";

/** Maximum recording duration in seconds */
const MAX_RECORDING_SECONDS = 30;

/** Timer interval in milliseconds */
const TIMER_INTERVAL_MS = 1000;

/** Number of frequency bins to sample for the audio waveform */
const AUDIO_LEVEL_BINS = 30;

/** Minimum interval between audio level updates to avoid excessive re-renders */
const AUDIO_LEVEL_THROTTLE_MS = 66;

/** Empty array returned when no audio analysis is active */
const EMPTY_LEVELS: readonly number[] = [];

/** Whether we're on a native platform that supports expo-speech-recognition */
const IS_NATIVE = Platform.OS === "ios" || Platform.OS === "android";

/**
 * Dynamically imports the native expo-speech-recognition module.
 * Cast needed because TypeScript resolves to web types which export
 * `typeof Class` (constructor) instead of instance type for the singleton.
 * This function only runs inside IS_NATIVE guards.
 * @returns The ExpoSpeechRecognitionModule singleton
 */
const getNativeSpeechModule = async () => {
  const mod = await import("expo-speech-recognition");
  return mod.ExpoSpeechRecognitionModule as any;
};

/**
 * Return type for useAudioRecorder hook
 */
interface UseAudioRecorderReturn {
  /** Start recording audio */
  readonly startRecording: () => Promise<void>;
  /** Stop recording and return base64 audio */
  readonly stopRecording: () => Promise<string | null>;
  /** Cancel recording without returning audio */
  readonly cancelRecording: () => Promise<void>;
  /** Whether microphone permission is granted */
  readonly hasPermission: boolean;
  /** Request microphone permission */
  readonly requestPermission: () => Promise<boolean>;
  /** Whether currently recording */
  readonly isRecording: boolean;
  /** Elapsed recording time in seconds */
  readonly elapsedSeconds: number;
  /** Live transcript from speech recognition */
  readonly liveTranscript: string;
  /** Real-time frequency levels from Web Audio API (0-1 range, ~30 bins) */
  readonly audioLevels: readonly number[];
}

/**
 * Converts a Blob to a base64 string.
 * @param blob - Audio blob from MediaRecorder
 * @returns Base64 encoded string
 */
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1] ?? "";
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

/**
 * Normalizes a volume dB value from expo-speech-recognition (-2 to 10)
 * into an array of bins (0-1 range) for waveform visualization.
 * @param value - Raw dB value from volumechange event
 * @returns Array of normalized frequency bins
 */
const volumeToLevels = (value: number): readonly number[] => {
  const normalized = Math.max(0, Math.min(1, (value + 2) / 12));
  const now = Date.now();
  return Array.from({ length: AUDIO_LEVEL_BINS }, (_, i) => {
    const variation = Math.sin(i * 0.7 + now * 0.005) * 0.15;
    return Math.max(0, Math.min(1, normalized + variation));
  });
};

/**
 * Hook for managing audio recording with real-time frequency analysis.
 * On web, uses MediaRecorder + SpeechRecognition + Web Audio AnalyserNode.
 * On native, uses expo-speech-recognition for on-device speech-to-text.
 * @param lang - BCP-47 language tag for speech recognition (e.g. "en-US", "es-ES")
 * @returns Recording controls and state including live audioLevels
 */
export /**
 *
 */
const useAudioRecorder = (lang: string = "en-US"): UseAudioRecorderReturn => {
  // 1. VARIABLES & STATE
  const [hasPermission, setHasPermission] = useState(!IS_NATIVE);
  const [isRecording, setIsRecording] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [liveTranscript, setLiveTranscript] = useState("");
  const [audioLevels, setAudioLevels] =
    useState<readonly number[]>(EMPTY_LEVELS);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recognitionRef = useRef<any>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Web Audio API refs for real-time frequency analysis
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animationFrameRef = useRef<number | null>(null);
  const lastLevelUpdateRef = useRef<number>(0);

  // Native speech recognition event listener subscriptions
  const nativeListenersRef = useRef<{ remove: () => void }[]>([]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    timerRef.current = setInterval(() => {
      setElapsedSeconds(prev => {
        const next = prev + 1;
        dictationElapsedSecondsVar(next);
        if (next >= MAX_RECORDING_SECONDS) {
          // Auto-stop handled by the component
        }
        return next;
      });
    }, TIMER_INTERVAL_MS);
  }, []);

  /**
   * Tears down the Web Audio analyser, cancels the animation frame loop,
   * and resets audioLevels to empty.
   */
  const stopAudioAnalysis = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(console.warn);
      audioContextRef.current = null;
    }
    analyserRef.current = null;
    setAudioLevels(EMPTY_LEVELS);
  }, []);

  /**
   * Removes all native expo-speech-recognition event listeners.
   */
  const removeNativeListeners = useCallback(() => {
    nativeListenersRef.current.forEach(sub => sub.remove());
    nativeListenersRef.current = [];
  }, []);

  /**
   * Creates an AudioContext + AnalyserNode from the MediaStream and starts
   * a requestAnimationFrame loop that reads frequency data at ~15fps.
   */
  const startAudioAnalysis = useCallback(
    (stream: MediaStream) => {
      try {
        const audioContext = new AudioContext();
        const source = audioContext.createMediaStreamSource(stream);
        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 64;
        source.connect(analyser);

        audioContextRef.current = audioContext;
        analyserRef.current = analyser;
        lastLevelUpdateRef.current = 0;

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateLevels = () => {
          if (!analyserRef.current) return;

          const now = performance.now();
          if (now - lastLevelUpdateRef.current >= AUDIO_LEVEL_THROTTLE_MS) {
            lastLevelUpdateRef.current = now;
            analyserRef.current.getByteFrequencyData(dataArray);

            const binCount = Math.min(AUDIO_LEVEL_BINS, dataArray.length);
            const levels: readonly number[] = Array.from(
              { length: binCount },
              (_, i) => (dataArray[i] ?? 0) / 255
            );
            setAudioLevels(levels);
          }

          animationFrameRef.current = requestAnimationFrame(updateLevels);
        };

        animationFrameRef.current = requestAnimationFrame(updateLevels);
      } catch (err) {
        console.warn("[useAudioRecorder] Failed to start audio analysis:", err);
        stopAudioAnalysis();
      }
    },
    [stopAudioAnalysis]
  );

  const stopSpeechRecognition = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
  }, []);

  const startSpeechRecognition = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = lang;

    recognition.onresult = (event: {
      results: { transcript: string; isFinal: boolean }[][];
    }) => {
      const results = Array.from(event.results);
      const transcript = results
        .map((result: { transcript: string }[]) => result[0]?.transcript ?? "")
        .join("");
      setLiveTranscript(transcript);
      dictationLiveTranscriptVar(transcript);
    };

    recognition.onerror = (event: { error: string }) => {
      console.warn("[useAudioRecorder] SpeechRecognition error:", event.error);
    };

    recognition.start();
    recognitionRef.current = recognition;
  }, [lang]);

  const requestPermission = useCallback(async (): Promise<boolean> => {
    if (!IS_NATIVE) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });
        stream.getTracks().forEach(track => track.stop());
        setHasPermission(true);
        return true;
      } catch {
        setHasPermission(false);
        return false;
      }
    }
    try {
      const speechModule = await getNativeSpeechModule();
      if (!speechModule.isRecognitionAvailable()) {
        console.warn(
          "[useAudioRecorder] Speech recognition is not available on this device"
        );
        setHasPermission(false);
        return false;
      }
      const { granted } = await speechModule.requestPermissionsAsync();
      setHasPermission(granted);
      return granted;
    } catch (error) {
      console.warn(
        "[useAudioRecorder] expo-speech-recognition not available on this build:",
        error
      );
      setHasPermission(false);
      return false;
    }
  }, []);

  const startRecording = useCallback(async () => {
    const granted = await requestPermission();
    if (!granted) return;

    setLiveTranscript("");
    dictationLiveTranscriptVar("");

    if (IS_NATIVE) {
      try {
        const speechModule = await getNativeSpeechModule();

        if (!speechModule.isRecognitionAvailable()) {
          console.warn(
            "[useAudioRecorder] Speech recognition is not available on this device"
          );
          return;
        }

        removeNativeListeners();

        const resultSub = speechModule.addListener(
          "result",
          (event: { isFinal: boolean; results: { transcript: string }[] }) => {
            const transcript = event.results.map(r => r.transcript).join("");
            setLiveTranscript(transcript);
            dictationLiveTranscriptVar(transcript);
          }
        );

        const volumeSub = speechModule.addListener(
          "volumechange",
          (event: { value: number }) => {
            setAudioLevels(volumeToLevels(event.value));
          }
        );

        const errorSub = speechModule.addListener(
          "error",
          (event: { error: string; message: string }) => {
            console.warn(
              "[useAudioRecorder] Speech recognition error:",
              event.error,
              event.message
            );
          }
        );

        const endSub = speechModule.addListener("end", () => {
          console.info("[useAudioRecorder] Speech recognition session ended");
        });

        nativeListenersRef.current = [resultSub, volumeSub, errorSub, endSub];

        speechModule.start({
          lang,
          interimResults: true,
          continuous: true,
          addsPunctuation: true,
          volumeChangeEventOptions: { enabled: true, intervalMillis: 100 },
        });
      } catch (error) {
        console.warn(
          "[useAudioRecorder] Native speech recognition not available:",
          error
        );
        return;
      }
    } else {
      // Web: use MediaRecorder + Web Audio AnalyserNode
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: "audio/webm",
      });
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) {
          chunksRef.current = [...chunksRef.current, event.data];
        }
      };

      mediaRecorder.start(1000); // Collect data every second
      mediaRecorderRef.current = mediaRecorder;

      // Start real-time frequency analysis from the same stream
      startAudioAnalysis(stream);

      // Start speech recognition for live transcript
      startSpeechRecognition();
    }

    setIsRecording(true);
    setElapsedSeconds(0);
    dictationElapsedSecondsVar(0);
    dictationStatusVar("recording");
    startTimer();
  }, [
    requestPermission,
    startTimer,
    startSpeechRecognition,
    startAudioAnalysis,
    removeNativeListeners,
    lang,
  ]);

  const stopRecording = useCallback(async (): Promise<string | null> => {
    clearTimer();
    setIsRecording(false);

    if (!IS_NATIVE) {
      // Web: stop MediaRecorder, SpeechRecognition, and audio analysis
      stopSpeechRecognition();
      stopAudioAnalysis();

      const mediaRecorder = mediaRecorderRef.current;
      if (!mediaRecorder) return null;

      return new Promise<string | null>(resolve => {
        mediaRecorder.onstop = async () => {
          const blob = new Blob(chunksRef.current, { type: "audio/webm" });
          mediaRecorder.stream.getTracks().forEach(track => track.stop());
          mediaRecorderRef.current = null;
          chunksRef.current = [];
          const base64 = await blobToBase64(blob);
          resolve(base64);
        };
        mediaRecorder.stop();
      });
    }

    // Native: stop speech recognition (triggers final result via listener)
    try {
      const speechModule = await getNativeSpeechModule();
      speechModule.stop();
    } catch (error) {
      console.warn("[useAudioRecorder] Native stop failed:", error);
    }

    removeNativeListeners();
    setAudioLevels(EMPTY_LEVELS);

    // No audio file on native — transcript is captured via liveTranscript state
    return null;
  }, [
    clearTimer,
    stopSpeechRecognition,
    stopAudioAnalysis,
    removeNativeListeners,
  ]);

  const cancelRecording = useCallback(async () => {
    clearTimer();

    if (!IS_NATIVE) {
      stopSpeechRecognition();
      stopAudioAnalysis();

      const mediaRecorder = mediaRecorderRef.current;
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
      }
      mediaRecorderRef.current = null;
      chunksRef.current = [];
    } else {
      try {
        const speechModule = await getNativeSpeechModule();
        speechModule.abort();
      } catch (error) {
        console.warn("[useAudioRecorder] Native cancel failed:", error);
      }
      removeNativeListeners();
    }

    setIsRecording(false);
    setElapsedSeconds(0);
    setLiveTranscript("");
    setAudioLevels(EMPTY_LEVELS);
    dictationElapsedSecondsVar(0);
    dictationLiveTranscriptVar("");
    dictationStatusVar("idle");
  }, [
    clearTimer,
    stopSpeechRecognition,
    stopAudioAnalysis,
    removeNativeListeners,
  ]);

  // 2. SIDE EFFECTS

  // Cleanup on unmount
  useEffect(
    () => () => {
      clearTimer();
      stopSpeechRecognition();
      stopAudioAnalysis();
      removeNativeListeners();
      if (
        mediaRecorderRef.current &&
        mediaRecorderRef.current.state !== "inactive"
      ) {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream
          ?.getTracks()
          .forEach(track => track.stop());
        mediaRecorderRef.current = null;
      }
    },
    [
      clearTimer,
      stopSpeechRecognition,
      stopAudioAnalysis,
      removeNativeListeners,
    ]
  );

  // Check permission on mount (native only)
  useEffect(() => {
    if (!IS_NATIVE) return;
    getNativeSpeechModule()
      .then(speechModule => speechModule.getPermissionsAsync())
      .then(({ granted }: { granted: boolean }) => {
        setHasPermission(granted);
      })
      .catch(console.warn);
  }, []);

  // 3. RETURN
  return useMemo(
    () => ({
      startRecording,
      stopRecording,
      cancelRecording,
      hasPermission,
      requestPermission,
      isRecording,
      elapsedSeconds,
      liveTranscript,
      audioLevels,
    }),
    [
      startRecording,
      stopRecording,
      cancelRecording,
      hasPermission,
      requestPermission,
      isRecording,
      elapsedSeconds,
      liveTranscript,
      audioLevels,
    ]
  );
};
/* eslint-enable functional/immutable-data -- Re-enable after browser API section */
/* eslint-enable @typescript-eslint/no-explicit-any -- Re-enable after browser API section */
